import { useState } from "react";
import { makePostApiCall } from "../../shared/makeApiCall";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import ChangeCustomerForm from "../../components/forms/orders/modals/ChangeCustomerForm";
import type { ChangeCustomerValues } from "../../components/forms/orders/modals/ChangeCustomerForm";
import type { BreadcrumbPage } from "../../components/controls/BreadCrumbs";
import type { EmptyModalState } from "../../components/modals/EmptyModal";
import type { SlideOverState } from "../../components/slideOver/SlideOver";
import type {
  CartItem, CartOrderDetails, CatalogCategory, CatalogProduct, CustomerOrderOption,
} from "../../types/orders";
import { validateOrderProducts } from "../../shared/orderAvailability";

interface CategoryOrProducts {
  type?: 1 | 2;
  items?: CatalogCategory[] | CatalogProduct[];
}

export function CreateOrderViewModel(
  cart: CartItem[],
  orderDetails: CartOrderDetails | null,
  modifyCart: (item: CartItem) => void,
  logout: () => void,
  setOrderDetails: (details: CartOrderDetails | null) => void,
  setCart: (cart: CartItem[]) => void,
) {
  const [emptyModal, setEmptyModelOpen] = useState<EmptyModalState>({ open: false, childComponent: null });
  const [loading, setLoading] = useState(false);
  const [slideOver, setSlideOver] = useState<SlideOverState>({ childComponent: null, open: false });
  const [customers, setCustomers] = useState<CustomerOrderOption[]>([]);
  const [primaryCategories, setPrimaryCategories] = useState<CatalogCategory[] | null>(null);
  const [subCategories, setSubCategories] = useState<CatalogCategory[] | null>(null);
  const [products, setProducts] = useState<CatalogProduct[] | null>(null);
  const [breadCrumbs, setBreadCrumbs] = useState<BreadcrumbPage[]>([]);
  const unauthorized = (status?: number) => { if (status === 401) logout(); };
  const closeModal = () => setEmptyModelOpen({ childComponent: null, open: false });
  const showErrorModal = (title = "Unable to load order catalog", subtitle?: string) => {
    setEmptyModelOpen({
      childComponent: <ModelErrorComponent setOpen={closeModal} subtitle={subtitle} title={title} />,
      open: true,
    });
  };

  const getCustomersForDropdown = async (search?: string) => {
    setLoading(true);
    const payload = { search: search ?? null };
    const result = await makePostApiCall<{ customers: CustomerOrderOption[] }, typeof payload>(
      "getCustomersForDropdown", payload,
    );
    setLoading(false);
    if (result.success) setCustomers(result.data.customers);
    else unauthorized(result.errorStatus);
  };

  const getPrimaryCategoriesForCustomerGroup = async (customerId: number) => {
    const payload = { customer_id: customerId };
    const result = await makePostApiCall<CatalogCategory[], typeof payload>(
      "getPrimaryCategoriesForCustomerGroup", payload,
    );
    if (result.success) {
      setPrimaryCategories(result.data);
      setSubCategories(null);
      setProducts(null);
      setBreadCrumbs([{ name: "Categories", href: "#", current: true,
        onClick: () => {
          setSubCategories(null); setProducts(null);
          setBreadCrumbs((current) => current.slice(0, 1));
        } }]);
    } else { unauthorized(result.errorStatus); showErrorModal(result.data.message); }
  };

  const onCustomerSelected = (payload: ChangeCustomerValues) => {
    const customer = payload.customer as CustomerOrderOption | null;
    if (!customer) return;
    const nextCustomerId = Number(customer.value);
    if (orderDetails?.customer_id && orderDetails.customer_id !== nextCustomerId) {
      setCart([]); setPrimaryCategories(null); setSubCategories(null); setProducts(null); setBreadCrumbs([]);
    }
    const selected: CartOrderDetails = {
      ...orderDetails,
      company_name: customer.label,
      customer_id: nextCustomerId,
      customer_group_id: customer.group_id ?? null,
      tax_rate: Number(customer.tax_rate ?? 0),
      city: customer.city, line1: customer.line1, line2: customer.line2,
      postal_code: customer.postal_code, province: customer.province,
      country_code: customer.country_code, po_number: payload.po_number,
      expected_date: payload.expected_date,
    };
    setOrderDetails(selected);
  };

  const revalidateCart = async (customerId: number) => {
    if (cart.length === 0) return;
    const result = await validateOrderProducts(customerId, cart.map((item) => item.id));
    if (!result.success) {
      unauthorized(result.errorStatus);
      showErrorModal(result.data.message);
      return;
    }

    const availableIds = new Set(result.data.available_product_ids);
    const availableCart = cart.filter((item) => availableIds.has(item.id));
    if (availableCart.length !== cart.length) {
      setCart(availableCart);
      showErrorModal(
        "Unavailable products were removed",
        result.data.unavailable_products.map((item) => item.product_name).join(", "),
      );
    }
  };

  const applyCartQuantities = (items: CatalogProduct[]) =>
    items.map((item) => {
      const existing = cart.find((cartItem) => cartItem.id === item.id);
      return existing ? {
        ...item,
        quantity: existing.quantity,
        price: existing.price,
        discount: existing.discount,
        tax_rate: existing.tax_rate,
        sub_dollar_amount: existing.sub_dollar_amount,
        total_dollar_amount: existing.total_dollar_amount,
        notes: existing.notes,
      } : item;
    });

  const getSubCategoriesOrProductsForPrimaryCategory = async (category: CatalogCategory) => {
    if (!orderDetails?.customer_id) return;
    const payload = { customer_id: orderDetails.customer_id };
    const result = await makePostApiCall<CategoryOrProducts, typeof payload>(
      `getSubCategoriesOrProductsForPrimaryCategory/${category.id}`, payload,
    );
    if (!result.success) { unauthorized(result.errorStatus); showErrorModal(result.data.message); return; }
    if (result.data.type === 1) {
      setSubCategories((result.data.items ?? []) as CatalogCategory[]);
      setBreadCrumbs((current) => [...current, { name: category.name, href: "#", current: true,
        onClick: () => { setProducts(null); setBreadCrumbs((pages) => pages.slice(0, -1)); } }]);
    } else if (result.data.type === 2) {
      setProducts(applyCartQuantities((result.data.items ?? []) as CatalogProduct[]));
      setBreadCrumbs((current) => [...current, { name: category.name, href: "#", current: true }]);
    } else showErrorModal("No subcategory or product was found.");
  };

  const getProductsForSubCategory = async (category: CatalogCategory) => {
    if (!orderDetails?.customer_id) return;
    const payload = { customer_id: orderDetails.customer_id };
    const result = await makePostApiCall<{ products: CatalogProduct[] }, typeof payload>(
      `getProductsForSubCategory/${category.id}`, payload,
    );
    if (result.success) {
      setProducts(applyCartQuantities(result.data.products));
      setBreadCrumbs((current) => [...current, { name: category.name, href: "#", current: true }]);
    } else { unauthorized(result.errorStatus); showErrorModal(result.data.message); }
  };

  const onProductQuantityChanged = (
    changedQuantity: number | string | null,
    product: CatalogProduct,
    index: number,
    increment: boolean,
    decrement: boolean,
  ) => {
    if (!products) return;
    const current = products[index];
    if (!current) return;
    const quantity = increment ? current.quantity + 1
      : decrement ? Math.max(0, current.quantity - 1) : Number(changedQuantity ?? 0);
    const discount = Number(current.discount ?? 0);
    const subtotal = quantity * (Number(current.price) - discount);
    const taxRate = Number(
      current.tax_rate ?? (current.taxable === 1 ? orderDetails?.tax_rate ?? 0 : 0),
    );
    const changed: CatalogProduct = {
      ...current, quantity, discount, tax_rate: taxRate, sub_dollar_amount: subtotal,
      total_dollar_amount: Number((subtotal * (1 + taxRate / 100)).toFixed(2)),
    };
    setProducts(products.map((item, itemIndex) => itemIndex === index ? changed : item));
    modifyCart(changed);
  };

  const openChangeCustomerModal = () => setEmptyModelOpen({
    childComponent: <ChangeCustomerForm customers={customers} onSubmit={onCustomerSelected}
      setOpen={(state) => setEmptyModelOpen({ childComponent: null, ...state })}
      orderDetails={orderDetails} />,
    open: true,
  });

  return {
    loading, setEmptyModelOpen, setSlideOver, slideOver, emptyModal, breadCrumbs,
    primaryCategories, subCategories, products,
    primaryCategorySelected: getSubCategoriesOrProductsForPrimaryCategory,
    subCategorySelected: getProductsForSubCategory, onProductQuantityChanged,
    openChangeCustomerModal, getPrimaryCategoriesForCustomerGroup,
    getCustomersForDropdown, revalidateCart,
  };
}
